import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AxiosRequestConfig } from 'axios';
import { filter, includes, kebabCase, omit, get } from 'lodash';

import providersResource from '@lib/resources/providersResource';
import { EntityID, Provider } from '@lib/resources/types';
import storageService from '@lib/services/storageService';
import { STORAGE_KEYS } from 'utils/constants';

import useProvidersByIDs from './useProviderSearchByIDs';

import FilterList, { FilterListProps } from '../FilterList';

const filterByName = (providerList: Provider[], searchTerm: string | undefined) =>
  searchTerm ? filter(providerList, (item) => includes(kebabCase(item.name), kebabCase(searchTerm))) : providerList;

interface Props extends FilterListProps {
  itemsIds?: EntityID[];
  isReferringProvider?: boolean;
  isRenderingProvider?: boolean;
  respectSchedulerSetting?: boolean;
  fetchPageConfig?: AxiosRequestConfig;
}

const FilterListProviders: React.FC<Props> = ({
  itemsIds,
  idsExcluded,
  fetchPageConfig,
  limit,
  respectSchedulerSetting,
  ...other
}) => {
  const PSFilter = storageService.get(STORAGE_KEYS.patientFormPSFilters);
  const providersFromPSFilterRaw = get(PSFilter, 'provider', []);
  const providersFromPSFilter = Array.isArray(providersFromPSFilterRaw) ? providersFromPSFilterRaw : [];
  const providers = useSelector(providersResource.selectEntitiesObj);
  const dispatch = useDispatch();
  const optionalParams: any = {};
  const [searchTerm, setSearchTerm] = useState<string | undefined>('');

  if (respectSchedulerSetting) {
    optionalParams['settings.scheduler.enabled'] = true;
    optionalParams.allProviders = true;
    optionalParams.visible = true;
    optionalParams.schedulable = true;
  }

  const shouldUseItemsFromProp = !!itemsIds;

  const { data: providersFromPSFilterData = [], isLoading: isLoadingProviders } = useProvidersByIDs(
    {
      ids: providersFromPSFilter,
      // if the PSFilter doesnt have any providers, we will use the limit, otherwise, we show all the providres from the PSFilter
      ...(providersFromPSFilter.length === 0 && { limit, name: searchTerm }),
      ...optionalParams,
    },
    {
      enabled: !shouldUseItemsFromProp,
    },
  );

  const itemsFromProp = filter(providers, ({ id }) => includes(itemsIds, id));
  const filteredItemsFromProp = filterByName(itemsFromProp, searchTerm);

  const normalizedProvidersFromPSFilter = providersFromPSFilterData.map((it) => ({ ...it, id: it._id }));
  const filteredProvidersFromPSFilter = filterByName(normalizedProvidersFromPSFilter, searchTerm);

  useEffect(() => {
    dispatch(
      providersResource.fetchBulk({
        ids: itemsIds || idsExcluded,
      }),
    );
  }, [dispatch, itemsIds, idsExcluded]);

  const handleSearch = (_: unknown, __: unknown, name: string | undefined) => {
    setSearchTerm(name ?? '');
  };

  return (
    <FilterList
      hideSearchField={get(other, 'hideSearchField', false)}
      itemsLabel="providers"
      allItems={providers}
      items={shouldUseItemsFromProp ? filteredItemsFromProp.slice(0, limit) : filteredProvidersFromPSFilter}
      idsExcluded={idsExcluded}
      onSearchChange={handleSearch}
      limit={limit}
      loading={shouldUseItemsFromProp ? other.loading : isLoadingProviders}
      {...omit(other, 'allItems', 'items', 'isReferringProvider', 'isRenderingProvider')}
    />
  );
};

export default FilterListProviders;
